// Generated automatically with "fut". Do not edit.

/**
 * AES-128 block cipher.
 *
 * <p>Ported from the Stanford Javascript Crypto Library (SJCL):
 * https://github.com/bitwiseshiftleft/sjcl/blob/85caa53c/core/aes.js
 * <p>Original authors: Emily Stark, Mike Hamburg, Dan Boneh (Stanford University).
 * Original code is based on OpenSSL's aes_core.c, a public-domain implementation
 * by Vincent Rijmen, Antoon Bosselaers and Paulo Barreto.
 * <p>Copyright (c) 2009-2015, Emily Stark, Mike Hamburg and Dan Boneh at
 * Stanford University. All rights reserved.
 * Licensed under the BSD 2-Clause License:
 * https://opensource.org/licenses/BSD-2-Clause
 * <p>Only encryption is implemented, as AES-CCM CTR mode uses the block cipher
 * in the encrypt direction for both encryption and decryption.
 */
export class Aes128 {
    /**
   * @readonly
   * @type {Int32Array}
   */
    _encKey = new Int32Array(44);
    /**
   * @readonly
   * @type {Int32Array}
   */
    _encTable0 = new Int32Array(256);
    /**
   * @readonly
   * @type {Int32Array}
   */
    _encTable1 = new Int32Array(256);
    /**
   * @readonly
   * @type {Int32Array}
   */
    _encTable2 = new Int32Array(256);
    /**
   * @readonly
   * @type {Int32Array}
   */
    _encTable3 = new Int32Array(256);
    /**
   * @readonly
   * @type {Int32Array}
   */
    _sbox = new Int32Array(256);

    /**
     * Initializes AES-128 with the given 16-byte key.
     * Must be called before <code>EncryptBlock</code>.
     * Runs table pre-computation and key schedule.
     * Ported from sjcl.cipher.aes constructor and _precompute:
     * https://github.com/bitwiseshiftleft/sjcl/blob/85caa53c/core/aes.js_L25
   * @public
   * @param {Readonly<Uint8Array>} key The 16-byte AES key.
   * @param {number} keyOffset Offset into <code>key</code>.
   */
    initialize(key, keyOffset) {
        this._precompute();
        this._scheduleKey(key, keyOffset);
    }

    /**
     * Shifts a byte value (0-255) into the top byte of a 32-bit signed int: result = b &lt;&lt; 24.
     * Avoids signed-integer overflow (which is undefined behaviour in C and not guaranteed
     * to wrap in Fusion/Python) by splitting around the sign bit:
     * (b &amp; 0x7F) &lt;&lt; 24  is at most 0x7F000000 = 2130706432, which fits in int.
     * If bit 7 of b is set we subtract 2147483648 (INT_MIN) to set the sign bit.
   * @param {number} b
   */
    static _byteToTop(b) {
        let hi = (b & 127) << 24;
        if ((b & 128) != 0)
            hi = Number(hi - 2147483648);
        return hi;
    }

    /**
     * Rotates a 32-bit integer left by 8 bits (= right by 24): [b3,b2,b1,b0] -&gt; [b2,b1,b0,b3].
     * Equivalent to <code>x &lt;&lt; 24 ^ x &gt;&gt; 8 &amp; 0xFFFFFF</code>, but calls ByteToTop to avoid overflow.
   * @param {number} x
   */
    static _rot8Left(x) {
        return Aes128._byteToTop(x & 255) ^ (x >> 8 & 16777215);
    }

    /**
     * Loads a big-endian 32-bit word from <code>buf[o..o+4)</code>.
   * @param {Readonly<Uint8Array>} buf
   * @param {number} o
   */
    static _loadWord(buf, o) {
        return Aes128._byteToTop(buf[o] & 255) | (buf[o + 1] & 255) << 16 | (buf[o + 2] & 255) << 8 | (buf[o + 3] & 255);
    }

    /**
     * Stores a big-endian 32-bit word into <code>buf[o..o+4)</code>.
   * @param {Uint8Array} buf
   * @param {number} o
   * @param {number} w
   */
    static _storeWord(buf, o, w) {
        buf[o] = w >> 24 & 255;
        buf[o + 1] = w >> 16 & 255;
        buf[o + 2] = w >> 8 & 255;
        buf[o + 3] = w & 255;
    }

    /**
     * Pre-computes the AES S-box and MixColumns tables.
     *
     * <p>Ported from sjcl.cipher.aes._precompute:
     * https://github.com/bitwiseshiftleft/sjcl/blob/85caa53c/core/aes.js_L114
     * <p>The loop mirrors sjcl's <code>for (x = xInv = 0; !sbox[x]; x ^= x2 || 1, xInv = th[xInv] || 1)</code>.
     * It processes all 256 GF(2^8) elements and stops when sbox[x] is already populated.
     */
    _precompute() {
        this._sbox.fill(0);
        const d = new Int32Array(256);
        const th = new Int32Array(256);
        for (let i = 0; i < 256; i++) {
            d[i] = i << 1 ^ (i >> 7) * 283;
            th[d[i] ^ i] = i;
        }
        let x = 0;
        let xInv = 0;
        for (;;) {
            if (this._sbox[x] != 0)
                break;
            let s = xInv ^ xInv << 1 ^ xInv << 2 ^ xInv << 3 ^ xInv << 4;
            s = s >> 8 ^ (s & 255) ^ 99;
            this._sbox[x] = s;
            let x2 = d[x];
            let x4 = d[x2];
            let x8 = d[x4];
            let tEnc = d[s] * 257 ^ s * 16843008;
            tEnc = Aes128._rot8Left(tEnc);
            this._encTable0[x] = tEnc;
            tEnc = Aes128._rot8Left(tEnc);
            this._encTable1[x] = tEnc;
            tEnc = Aes128._rot8Left(tEnc);
            this._encTable2[x] = tEnc;
            tEnc = Aes128._rot8Left(tEnc);
            this._encTable3[x] = tEnc;
            let x2or1 = x2;
            if (x2or1 == 0)
                x2or1 = 1;
            x ^= x2or1;
            let nextXInv = th[xInv];
            if (nextXInv == 0)
                nextXInv = 1;
            xInv = nextXInv;
        }
    }

    /**
     * Expands the 16-byte AES-128 key into the 44-word encryption key schedule.
     * Ported from the key expansion loop in sjcl.cipher.aes constructor:
     * https://github.com/bitwiseshiftleft/sjcl/blob/85caa53c/core/aes.js_L42
   * @param {Readonly<Uint8Array>} key
   * @param {number} keyOffset
   */
    _scheduleKey(key, keyOffset) {
        for (let i = 0; i < 4; i++) {
            this._encKey[i] = Aes128._loadWord(key, keyOffset + i * 4);
        }
        let rcon = 1;
        for (let i = 4; i < 44; i++) {
            let tmp = this._encKey[i - 1];
            if (i % 4 == 0) {
                tmp = Aes128._byteToTop(this._sbox[tmp >> 16 & 255]) ^ this._sbox[tmp >> 8 & 255] << 16 ^ this._sbox[tmp & 255] << 8 ^ this._sbox[tmp >> 24 & 255] ^ Aes128._byteToTop(rcon);
                rcon = rcon << 1 ^ (rcon >> 7) * 283;
            }
            this._encKey[i] = this._encKey[i - 4] ^ tmp;
        }
    }

    /**
     * Encrypts one 16-byte block in-place (big-endian word order).
     * Ported from sjcl.cipher.aes._crypt (encrypt direction, dir=0):
     * https://github.com/bitwiseshiftleft/sjcl/blob/85caa53c/core/aes.js_L156
   * @public
   * @param {Uint8Array} block 16-byte block buffer. Encrypted in-place.
   * @param {number} offset Offset into <code>block</code>.
   */
    encryptBlock(block, offset) {
        let a = Aes128._loadWord(block, offset) ^ this._encKey[0];
        let b = Aes128._loadWord(block, offset + 4) ^ this._encKey[1];
        let c = Aes128._loadWord(block, offset + 8) ^ this._encKey[2];
        let d = Aes128._loadWord(block, offset + 12) ^ this._encKey[3];
        let a2;
        let b2;
        let c2;
        let kIndex = 4;
        for (let i = 0; i < 9; i++) {
            a2 = this._encTable0[a >> 24 & 255] ^ this._encTable1[b >> 16 & 255] ^ this._encTable2[c >> 8 & 255] ^ this._encTable3[d & 255] ^ this._encKey[kIndex];
            b2 = this._encTable0[b >> 24 & 255] ^ this._encTable1[c >> 16 & 255] ^ this._encTable2[d >> 8 & 255] ^ this._encTable3[a & 255] ^ this._encKey[kIndex + 1];
            c2 = this._encTable0[c >> 24 & 255] ^ this._encTable1[d >> 16 & 255] ^ this._encTable2[a >> 8 & 255] ^ this._encTable3[b & 255] ^ this._encKey[kIndex + 2];
            d = this._encTable0[d >> 24 & 255] ^ this._encTable1[a >> 16 & 255] ^ this._encTable2[b >> 8 & 255] ^ this._encTable3[c & 255] ^ this._encKey[kIndex + 3];
            kIndex += 4;
            a = a2;
            b = b2;
            c = c2;
        }
        let out0 = Aes128._byteToTop(this._sbox[a >> 24 & 255]) ^ this._sbox[b >> 16 & 255] << 16 ^ this._sbox[c >> 8 & 255] << 8 ^ this._sbox[d & 255] ^ this._encKey[kIndex];
        let out1 = Aes128._byteToTop(this._sbox[b >> 24 & 255]) ^ this._sbox[c >> 16 & 255] << 16 ^ this._sbox[d >> 8 & 255] << 8 ^ this._sbox[a & 255] ^ this._encKey[kIndex + 1];
        let out2 = Aes128._byteToTop(this._sbox[c >> 24 & 255]) ^ this._sbox[d >> 16 & 255] << 16 ^ this._sbox[a >> 8 & 255] << 8 ^ this._sbox[b & 255] ^ this._encKey[kIndex + 2];
        let out3 = Aes128._byteToTop(this._sbox[d >> 24 & 255]) ^ this._sbox[a >> 16 & 255] << 16 ^ this._sbox[b >> 8 & 255] << 8 ^ this._sbox[c & 255] ^ this._encKey[kIndex + 3];
        Aes128._storeWord(block, offset, out0);
        Aes128._storeWord(block, offset + 4, out1);
        Aes128._storeWord(block, offset + 8, out2);
        Aes128._storeWord(block, offset + 12, out3);
    }
}

/**
 * AES-CCM CTR mode for encryption and decryption.
 *
 * <p>Ported from the Stanford Javascript Crypto Library (SJCL):
 * https://github.com/bitwiseshiftleft/sjcl/blob/85caa53/core/ccm.js
 * <p>Specifically, this is a port of sjcl.mode.ccm._ctrMode and
 * sjcl.mode.ccm._computeTag, adapted to operate directly on byte arrays
 * rather than sjcl's BitArray representation.
 * <p>Original authors: Emily Stark, Mike Hamburg, Dan Boneh (Stanford University).
 * Special thanks to Roy Nicholson for pointing out a bug in the original.
 * <p>Copyright (c) 2009-2015, Emily Stark, Mike Hamburg and Dan Boneh at
 * Stanford University. All rights reserved.
 * Licensed under the BSD 2-Clause License:
 * https://opensource.org/licenses/BSD-2-Clause
 */
export class AesCcmCtr {
    /**
   * @private
   */
    constructor() {
    }

    /**
     * Runs AES-CCM CTR mode on <code>data</code>, XORing each 16-byte block with
     * AES(key, counter), where the counter is initialized from the nonce
     * and incremented for each block.
     *
     * <p>This is used for both encryption and decryption (CTR mode is symmetric).
     * Tag verification is intentionally skipped — see the 3DS AES CCM errata:
     * https://www.3dbrew.org/wiki/AES_Registers_CCM_mode_pitfall
     * <p>Ported from sjcl.mode.ccm._ctrMode:
     * https://github.com/bitwiseshiftleft/sjcl/blob/85caa53c/core/ccm.js_L194
     * <p>Counter block layout (16 bytes):
     * [0]      = L - 1  (flags byte; L=3 for 3DS so this is 0x02)
     * [1..12)  = nonce (11 bytes, nonce is clamped to 8*(15-L) = 96 bits)
     * [12..16) = 0x00000000  (counter, starts at 0 for tag, 1 for data)
     * <p>The data counter starts at 1 (counter 0 is reserved for the tag).
   * @public
   * @param {Aes128} aes Aes128 instance initialized with the key.
   * @param {Uint8Array} data Data buffer to XOR in-place (plaintext for encrypt, ciphertext for decrypt).
   * @param {number} dataOffset Offset into <code>data</code>.
   * @param {number} dataLength Number of bytes to process.
   * @param {Readonly<Uint8Array>} nonce Nonce buffer. Must be at least <code>nonceLength</code> bytes at <code>nonceOffset</code>.
   * @param {number} nonceOffset Offset into <code>nonce</code>.
   * @param {number} nonceLength Length of the nonce in bytes. For 3DS CCM this is 12, but only
   * 11 bytes are used after clamping to 8*(15-L) bits with L=3.
   */
    static ctrXor(aes, data, dataOffset, dataLength, nonce, nonceOffset, nonceLength) {
        const ctr = new Uint8Array(16);
        ctr[0] = 2;
        let nonceBytes = nonceLength;
        if (nonceBytes > 11)
            nonceBytes = 11;
        ctr.set(nonce.subarray(nonceOffset, nonceOffset + nonceBytes), 1);
        ctr[15] = 1;
        const block = new Uint8Array(16);
        let processed = 0;
        for (;;) {
            if (processed >= dataLength)
                break;
            block.set(ctr);
            aes.encryptBlock(block, 0);
            let blockLen = dataLength - processed;
            if (blockLen > 16)
                blockLen = 16;
            for (let j = 0; j < blockLen; j++) {
                data[dataOffset + processed + j] ^= block[j];
            }
            processed += blockLen;
            ctr[15]++;
            if (ctr[15] == 0) {
                ctr[14]++;
                if (ctr[14] == 0) {
                    ctr[13]++;
                    if (ctr[13] == 0) {
                        ctr[12]++;
                    }
                }
            }
        }
    }

    /**
     * Computes the AES-CCM CBC-MAC authentication tag over <code>data</code>.
     *
     * <p>Ported from sjcl.mode.ccm._computeTag and _macAdditionalData (no adata):
     * https://github.com/bitwiseshiftleft/sjcl/blob/85caa53c/core/ccm.js_L156
     * <p>The tag is then encrypted with CTR counter 0 (before the data blocks).
     * The result is written into <code>tagOut</code>.
   * @public
   * @param {Aes128} aes Aes128 instance initialized with the key.
   * @param {Readonly<Uint8Array>} data Plaintext data to MAC.
   * @param {number} dataOffset Offset into <code>data</code>.
   * @param {number} dataLength Length of data.
   * @param {Readonly<Uint8Array>} nonce Nonce buffer.
   * @param {number} nonceOffset Offset into <code>nonce</code>.
   * @param {number} nonceLength Length of nonce in bytes.
   * @param {number} tagLength Desired tag length in bytes (must be even, 4..16).
   * @param {Uint8Array} tagOut Output buffer for the encrypted tag. Must be at least <code>tagLength</code> bytes.
   * @param {number} tagOutOffset Offset into <code>tagOut</code>.
   */
    static computeTag(aes, data, dataOffset, dataLength, nonce, nonceOffset, nonceLength, tagLength, tagOut, tagOutOffset) {
        let l = 3;
        let flags = ((tagLength / 2 | 0) - 1) << 3 | (l - 1);
        const mac = new Uint8Array(16);
        mac[0] = flags;
        let nonceBytes = nonceLength;
        if (nonceBytes > 11)
            nonceBytes = 11;
        mac.set(nonce.subarray(nonceOffset, nonceOffset + nonceBytes), 1);
        mac[13] = dataLength >> 16 & 255;
        mac[14] = dataLength >> 8 & 255;
        mac[15] = dataLength & 255;
        aes.encryptBlock(mac, 0);
        const block = new Uint8Array(16);
        let processed = 0;
        for (;;) {
            if (processed >= dataLength)
                break;
            let blockLen = dataLength - processed;
            if (blockLen > 16)
                blockLen = 16;
            block.fill(0);
            block.set(data.subarray(dataOffset + processed, dataOffset + processed + blockLen));
            for (let j = 0; j < 16; j++) {
                mac[j] ^= block[j];
            }
            aes.encryptBlock(mac, 0);
            processed += blockLen;
        }
        const ctr0 = new Uint8Array(16);
        ctr0[0] = 2;
        let nb = nonceLength;
        if (nb > 11)
            nb = 11;
        ctr0.set(nonce.subarray(nonceOffset, nonceOffset + nb), 1);
        aes.encryptBlock(ctr0, 0);
        for (let i = 0; i < tagLength; i++) {
            tagOut[tagOutOffset + i] = mac[i] ^ ctr0[i];
        }
    }
}

/**
 * Abstract AES-CCM cipher interface for encrypting and decrypting wrapped Mii data.
 * Implementations may choose to support only encryption or only decryption,
 * in which case the unsupported method should assert false.
 * @abstract
 */
export class AesCcmCipher {
}

/**
 * Concrete <code>AesCcmCipher</code> implementation backed by the built-in <code>Aes128</code> + <code>AesCcmCtr</code>.
 */
export class NativeAesCcmCipher extends AesCcmCipher {
    /**
   * @readonly
   * @type {Aes128}
   */
    _aes = new Aes128();

    /**
     * Initializes the cipher with the given 16-byte key.
   * @public
   * @param {Readonly<Uint8Array>} key
   * @param {number} keyOffset
   */
    initialize(key, keyOffset) {
        this._aes.initialize(key, keyOffset);
    }

    /**
   * @public
   * @param {Uint8Array} dst
   * @param {number} dstOffset
   * @param {Readonly<Uint8Array>} content
   * @param {number} contentOffset
   * @param {number} contentLength
   * @param {Readonly<Uint8Array>} nonce
   * @param {number} nonceOffset
   * @param {number} nonceLength
   * @param {number} tagLength
   */
    encrypt(dst, dstOffset, content, contentOffset, contentLength, nonce, nonceOffset, nonceLength, tagLength) {
        dst.set(content.subarray(contentOffset, contentOffset + contentLength), dstOffset);
        AesCcmCtr.computeTag(this._aes, dst, dstOffset, contentLength, nonce, nonceOffset, nonceLength, tagLength, dst, dstOffset + contentLength);
        AesCcmCtr.ctrXor(this._aes, dst, dstOffset, contentLength, nonce, nonceOffset, nonceLength);
    }

    /**
   * @public
   * @param {Uint8Array} dst
   * @param {number} dstOffset
   * @param {Readonly<Uint8Array>} ciphertext
   * @param {number} ciphertextOffset
   * @param {number} ciphertextLength
   * @param {Readonly<Uint8Array>} nonce
   * @param {number} nonceOffset
   * @param {number} nonceLength
   * @param {number} tagLength
   */
    decryptSkipTag(dst, dstOffset, ciphertext, ciphertextOffset, ciphertextLength, nonce, nonceOffset, nonceLength, tagLength) {
        dst.set(ciphertext.subarray(ciphertextOffset, ciphertextOffset + ciphertextLength), dstOffset);
        AesCcmCtr.ctrXor(this._aes, dst, dstOffset, ciphertextLength, nonce, nonceOffset, nonceLength);
    }
}

/**
 * Handles encryption and decryption of the wrapped Mii data format used in QR codes.
 *
 * <p>This format is known as CFLiWrappedMiiData / FFLiWrappedStoreData.
 * <p>References (Credits: 3DBrew contributors, jaames, kazuki-4ys):
 * <ul>
 * <li>https://www.3dbrew.org/wiki/Mii_Maker_Mii_QR_Code_format</li>
 * <li>https://gist.github.com/jaames/96ce8daa11b61b758b6b0227b55f9f78</li>
 * <li>https://github.com/kazuki-4ys/kazuki-4ys.github.io/blob/148dc339974f8b7515bfdc1395ec1fc9becb68ab/web_apps/MiiInfoEditorCTR/encode.js</li>
 * <li>CFL: CFLi_WrapMiiData / CFLi_UnwrapMiiData</li>
 * <li>FFL: FFLiWrapStoreData / FFLiUnwrapStoreData</li>
 * </ul>
 */
export class WrappedStoreData {
    /**
   * @private
   */
    constructor() {
    }

    /**
     * Size of encrypted Mii data found in QR codes (CFLiWrappedMiiData, FFLiWrappedStoreData).
   * @public
   * @readonly
   * @type {number}
   */
    static LENGTH = 112;

    /**
     * Size of 3DS/Wii U format Mii data (FFLStoreData, CFLiMiiDataPacket, nn::mii::Ver3StoreData).
   * @public
   * @readonly
   * @type {number}
   */
    static STORE_DATA_LENGTH = 96;

    /**
     * Size of the AES-CCM nonce (IV) within wrapped data.
   * @readonly
   * @type {number}
   */
    static _NONCE_LENGTH = 12;

    /**
     * Size of the AES-CCM authentication tag (MAC) within wrapped data.
   * @readonly
   * @type {number}
   */
    static _TAG_LENGTH = 16;

    /**
     * Offset within StoreData of the ID field used to form the nonce (CreateID).
   * @readonly
   * @type {number}
   */
    static _ID_OFFSET = 12;

    /**
     * Number of bytes of the ID copied into the nonce.
     * The CreateID is 10 bytes, but it is truncated to be a multiple of 4
     * and no larger than the nonce, so it is 8 bytes.
   * @readonly
   * @type {number}
   */
    static _ID_LENGTH = 8;

    /**
     * Gets 96-byte 3DS/Wii U format Mii data from wrapped QR code data.
     *
     * <p>Decrypts AES-CCM encrypted data (CFLiWrappedMiiData) using the provided cipher.
     * <p>The default AES-CCM tag verification fails due to the following errata:
     * https://www.3dbrew.org/wiki/AES_Registers_CCM_mode_pitfall
     * Therefore tag verification is skipped. You MUST verify the CRC-16 of
     * the output to ensure the data is valid.
     * <p>Layout of <code>encryptedData</code> (112 bytes total):
     * <ul>
     * <li>[0..8)   ID / nonce prefix (8 bytes, copied verbatim into output at [12..20))</li>
     * <li>[8..96)  ciphertext (88 bytes = StoreDataLength - IdLength)</li>
     * <li>[96..112) authentication tag (16 bytes, not verified)</li>
     * </ul>
   * @public
   * @param {Uint8Array} dst Destination for decrypted StoreData. Must be <code>StoreDataLength</code> (96) bytes.
   * @param {number} dstOffset Offset into <code>dst</code>.
   * @param {Readonly<Uint8Array>} encryptedData Encrypted wrapped Mii QR code data (CFLiWrappedMiiData). Must be at least 112 bytes.
   * @param {number} encryptedOffset Offset into <code>encryptedData</code>.
   * @param {AesCcmCipher} cipher AES-CCM cipher implementation to use for decryption.
   */
    static decrypt(dst, dstOffset, encryptedData, encryptedOffset, cipher) {
        const nonce = new Uint8Array(12);
        nonce.set(encryptedData.subarray(encryptedOffset, encryptedOffset + 8));
        let ciphertextLength = 88;
        const decrypted = new Uint8Array(88);
        cipher.decryptSkipTag(decrypted, 0, encryptedData, encryptedOffset + 8, ciphertextLength, nonce, 0, 12, 16);
        dst.set(decrypted.subarray(0, 12), dstOffset);
        dst.set(nonce.subarray(0, 8), dstOffset + 12);
        dst.set(decrypted.subarray(12, 12 + ciphertextLength - 12), dstOffset + 12 + 8);
    }

    /**
     * Encrypts 3DS/Wii U Mii StoreData into the wrapped format (CFLiWrappedMiiData)
     * for use in a Mii QR code.
     *
     * <p>Layout of output <code>dst</code> (112 bytes total):
     * <ul>
     * <li>[0..8)   ID / nonce prefix (from storeData[12..20))</li>
     * <li>[8..96)  ciphertext (88 bytes)</li>
     * <li>[96..112) authentication tag (16 bytes)</li>
     * </ul>
   * @public
   * @param {Uint8Array} dst Destination for encrypted wrapped data. Must be 112 bytes.
   * @param {number} dstOffset Offset into <code>dst</code>.
   * @param {Readonly<Uint8Array>} storeData Input 3DS/Wii U StoreData. Must be <code>StoreDataLength</code> (96) bytes starting at <code>storeDataOffset</code>.
   * @param {number} storeDataOffset Offset into <code>storeData</code>.
   * @param {AesCcmCipher} cipher AES-CCM cipher implementation to use for encryption.
   */
    static encrypt(dst, dstOffset, storeData, storeDataOffset, cipher) {
        let idEndOffset = 20;
        const nonce = new Uint8Array(12);
        nonce.set(storeData.subarray(storeDataOffset + 12, storeDataOffset + 12 + 8));
        const content = new Uint8Array(96);
        content.set(storeData.subarray(storeDataOffset, storeDataOffset + 12));
        content.set(storeData.subarray(storeDataOffset + idEndOffset, storeDataOffset + idEndOffset + 96 - idEndOffset), 12);
        const encryptedBytes = new Uint8Array(112);
        cipher.encrypt(encryptedBytes, 0, content, 0, 96, nonce, 0, 12, 16);
        let correctEncryptedContentLength = 88;
        dst.set(nonce.subarray(0, 8), dstOffset);
        dst.set(encryptedBytes.subarray(0, correctEncryptedContentLength), dstOffset + 8);
        dst.set(encryptedBytes.subarray(96, 112), dstOffset + 96);
    }
}

export class WrappedStoreDataTest {

    /**
   * @readonly
   * @type {Readonly<Uint8Array>}
   */
    static _WRAP_KEY_DEVELOPMENT = new Uint8Array([18, 223, 146, 182, 255, 212, 56, 171, 41, 28, 79, 212, 215, 206, 37, 109]);

    /**
   * @readonly
   * @type {Readonly<Uint8Array>}
   */
    static _WRAP_TEST_DATA = new Uint8Array([3, 0, 0, 48, 223, 154, 52, 2, 131, 165, 234, 189, 144, 241, 7, 220,
        120, 162, 160, 53, 216, 164, 0, 0, 1, 0, 81, 48, 0, 0, 0, 0,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 64, 64,
        0, 0, 12, 1, 4, 104, 67, 24, 32, 52, 70, 20, 129, 18, 23, 104,
        13, 0, 0, 41, 0, 82, 72, 80, 0, 0, 0, 0, 0, 0, 0, 0,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 65, 200]);

    /**
   * @readonly
   * @type {Readonly<Uint8Array>}
   */
    static _WRAP_EXPECTED = new Uint8Array([144, 241, 7, 220, 120, 162, 160, 53, 255, 197, 89, 28, 47, 51, 192, 18,
        5, 139, 52, 86, 184, 185, 164, 113, 113, 109, 56, 161, 6, 122, 20, 145,
        35, 23, 132, 182, 82, 5, 169, 255, 169, 40, 21, 59, 107, 168, 156, 139,
        163, 255, 179, 59, 117, 11, 201, 3, 234, 37, 99, 164, 228, 14, 87, 168,
        161, 221, 210, 52, 194, 214, 103, 27, 133, 26, 208, 25, 47, 196, 121, 213,
        187, 121, 250, 69, 226, 12, 1, 234, 154, 68, 54, 41, 243, 203, 24, 163,
        248, 17, 248, 142, 190, 95, 25, 38, 162, 103, 177, 151, 240, 122, 13, 167]);

    /**
   * @param {Readonly<Uint8Array>} a
   * @param {Readonly<Uint8Array>} b
   * @param {number} size
   */
    static _compareByteArraysForTest(a, b, size) {
        for (let i = 0; i < size; i++) {
            if (a[i] != b[i]) {
                console.log("MISMATCH! Array A:");
                let hexA = "";
                let hexB = "";
                for (let i = 0; i < size; i++) {
                    hexA += `${a[i].toString(16).toUpperCase().padStart(2, "0")} `;
                    hexB += `${b[i].toString(16).toUpperCase().padStart(2, "0")} `;
                }
                console.log(hexA);
                console.log(`vs. Array B (mismatching at ${i}):`);
                console.log(hexB);
                throw new Error(`Bytes are mismatching at position ${i}.`);
            }
        }
    }

    /**
   * @public
   * @param {AesCcmCipher} cipher
   */
    static testEncrypt(cipher) {
        const wrapped = new Uint8Array(112);
        WrappedStoreData.encrypt(wrapped, 0, WrappedStoreDataTest._WRAP_TEST_DATA, 0, cipher);
        WrappedStoreDataTest._compareByteArraysForTest(wrapped, WrappedStoreDataTest._WRAP_EXPECTED, 112);
    }

    /**
   * @public
   * @param {AesCcmCipher} cipher
   */
    static testDecrypt(cipher) {
        const unwrapped = new Uint8Array(96);
        WrappedStoreData.decrypt(unwrapped, 0, WrappedStoreDataTest._WRAP_EXPECTED, 0, cipher);
        WrappedStoreDataTest._compareByteArraysForTest(unwrapped, WrappedStoreDataTest._WRAP_TEST_DATA, 96);
    }

    /**
   * @public
   */
    static main() {
        const cipher = new NativeAesCcmCipher();
        cipher.initialize(WrappedStoreDataTest._WRAP_KEY_DEVELOPMENT, 0);
        WrappedStoreDataTest.testEncrypt(cipher);
        WrappedStoreDataTest.testDecrypt(cipher);
        console.log("Encrypt & decrypt tests passed.");
        return 0;
    }
}

// process.exit(WrappedStoreDataTest.main());
